import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ClientIndicators {
  hasApprovedRenegotiation: boolean;
  activePromise: { date: string; amount: number } | null;
  brokenPromiseCount: number;
}

export function useCollectionIndicators(contactIds: string[]) {
  return useQuery({
    queryKey: ['collection-indicators', contactIds],
    queryFn: async () => {
      if (!contactIds.length) return {};
      
      // 1. Get approved renegotiations
      const { data: renegotiations } = await supabase
        .from('renegotiations')
        .select('contact_id')
        .in('contact_id', contactIds)
        .eq('status', 'approved');
      
      const renegotiatedContacts = new Set(renegotiations?.map(r => r.contact_id) || []);
      
      // 2. Get active promises (promised_date >= today, not yet broken)
      const today = new Date().toISOString().split('T')[0];
      const { data: activePromises } = await supabase
        .from('collection_history')
        .select('contact_id, promised_date, promised_amount')
        .in('contact_id', contactIds)
        .eq('outcome', 'promise_to_pay')
        .gte('promised_date', today)
        .order('promised_date', { ascending: true });
      
      // Group by contact (keep first/next promise)
      const promisesByContact = new Map<string, { date: string; amount: number }>();
      activePromises?.forEach(p => {
        if (p.contact_id && p.promised_date && !promisesByContact.has(p.contact_id)) {
          promisesByContact.set(p.contact_id, {
            date: p.promised_date,
            amount: p.promised_amount || 0,
          });
        }
      });
      
      // 3. Get broken promises (promised_date < today and no payment received after)
      const { data: brokenPromises } = await supabase
        .from('collection_history')
        .select('contact_id, promised_date')
        .in('contact_id', contactIds)
        .eq('outcome', 'promise_to_pay')
        .lt('promised_date', today);
      
      // Count broken promises per contact
      const brokenCountByContact = new Map<string, number>();
      brokenPromises?.forEach(p => {
        if (p.contact_id) {
          brokenCountByContact.set(
            p.contact_id, 
            (brokenCountByContact.get(p.contact_id) || 0) + 1
          );
        }
      });
      
      // Build result map
      const result: Record<string, ClientIndicators> = {};
      contactIds.forEach(id => {
        result[id] = {
          hasApprovedRenegotiation: renegotiatedContacts.has(id),
          activePromise: promisesByContact.get(id) || null,
          brokenPromiseCount: brokenCountByContact.get(id) || 0,
        };
      });
      
      return result;
    },
    enabled: contactIds.length > 0,
    staleTime: 30000, // 30 seconds
  });
}
